/*
 * NG+ sticky add to cart (snippets/ng-sticky-atc.liquid).
 *
 * A bar that slides up once the product's own Add to cart button has scrolled
 * out of view. It owns no cart logic: its button clicks the section's main
 * button, so lanyard mode, colors, subscriptions and app line-item
 * properties all go through the one add-to-cart path the page already has.
 * It mirrors the page's price, selection, image and availability.
 *
 * Hooks, all inside the same product section as the bar:
 *   [data-ng-sticky-main]     the page's Add to cart button (required)
 *   [data-ng-sticky-price]    the page's .price block, or a wrapper around it
 *   [data-ng-sticky-image]    an <img> to mirror (optional)
 *   [data-ng-sticky-summary]  attribute whose value is the selection summary,
 *                             e.g. "Red · Black lanyard"; the page keeps it
 *                             current. On Dawn product pages the bar keeps
 *                             its own copy from variant-change events.
 */
(function () {
  if (window.ngStickyAtcLoaded) return;
  window.ngStickyAtcLoaded = true;

  function priceText(source) {
    if (!source) return '';
    var block = source.classList.contains('price') ? source : source.querySelector('.price');
    if (!block) return '';
    var el = block.classList.contains('price--on-sale')
      ? block.querySelector('.price__sale .price-item--last')
      : block.querySelector('.price__regular .price-item--regular');
    // "$29.97 USD" -> "$29.97", to match the custom pages.
    return el ? el.textContent.replace(/\s*[A-Z]{3}\s*$/, '').trim() : '';
  }

  // Shopify CDN images resize by query string; the bar only needs a thumbnail.
  function thumb(src, width) {
    if (!src) return '';
    try {
      var url = new URL(src, window.location.href);
      if (/cdn\.shopify\.com|\/cdn\/shop\//.test(url.href)) url.searchParams.set('width', String(width || 120));
      return url.href;
    } catch (e) {
      return src;
    }
  }

  // Swap the bar's photo. image_tag rendered it with a srcset, and browsers
  // draw from srcset, not src, so both must change or the old photo stays up.
  function setImage(img, src) {
    if (!img || !src) return;
    var small = thumb(src, 120);
    if (img.getAttribute('src') === small) return;
    img.removeAttribute('sizes');
    img.setAttribute('srcset', small + ' 1x, ' + thumb(src, 240) + ' 2x, ' + thumb(src, 360) + ' 3x');
    img.setAttribute('src', small);
  }

  // The struck "was" price, when the page's price block shows one.
  function wasText(source) {
    if (!source) return '';
    var block = source.classList.contains('price') ? source : source.querySelector('.price');
    if (!block || !block.classList.contains('price--on-sale')) return '';
    var el = block.querySelector('.price__sale s') || block.querySelector('.price__sale .price-item--regular');
    return el ? el.textContent.replace(/\s*[A-Z]{3}\s*$/, '').trim() : '';
  }

  function setText(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }

  function init(bar) {
    if (bar.dataset.ngStickyReady) return;
    bar.dataset.ngStickyReady = 'true';

    var scope = bar.closest('.shopify-section') || document.body;
    var out = {
      price: bar.querySelector('[data-ng-sticky-out="price"]'),
      was: bar.querySelector('[data-ng-sticky-out="was"]'),
      summary: bar.querySelector('[data-ng-sticky-out="summary"]'),
      button: bar.querySelector('[data-ng-sticky-out="button"]'),
      image: bar.querySelector('.ng-sticky-atc__media'),
    };
    var addLabel = out.button.textContent.trim();
    var narrow = window.matchMedia('(max-width: 749px)');
    var pastMain = false;
    var pending = false;
    var cartUpdatedAt = 0;

    function main() {
      return scope.querySelector('[data-ng-sticky-main]');
    }

    function mainBusy(button) {
      return button.getAttribute('aria-disabled') === 'true' || button.classList.contains('loading');
    }

    function sync() {
      pending = false;
      var button = main();
      if (!button) return;

      var priceSource = scope.querySelector('[data-ng-sticky-price]');
      var price = priceText(priceSource);
      if (price) setText(out.price, price);
      if (out.was) {
        var was = wasText(priceSource);
        setText(out.was, was);
        out.was.hidden = !was;
      }

      var summarySource = scope.querySelector('[data-ng-sticky-summary]');
      setText(out.summary, summarySource ? summarySource.getAttribute('data-ng-sticky-summary') : '');

      // The src attribute, not currentSrc: pages swap src and currentSrc only
      // catches up once the new image has loaded.
      var image = scope.querySelector('[data-ng-sticky-image]');
      if (image) setImage(out.image, image.getAttribute('src'));

      var unavailable = button.disabled;
      out.button.disabled = unavailable;
      out.button.classList.toggle('loading', mainBusy(button));
      // Mirror the main button's words ("Add to cart", "Subscribe & save"),
      // without the price it repeats after the dash.
      var mainLabel = button.textContent.replace(/\s+/g, ' ').split(/\s[—–-]\s/)[0].trim();
      var label = mainLabel && mainLabel.length < 28 ? mainLabel : addLabel;
      // Phones: the dock has no room for a long label beside the price.
      if (narrow.matches && /^subscribe/i.test(label)) label = 'Subscribe';
      setText(out.button, unavailable ? (/sold out/i.test(button.textContent) ? 'Sold out' : 'Unavailable') : label);
      onScroll();
    }

    function schedule() {
      if (pending) return;
      pending = true;
      requestAnimationFrame(sync);
    }

    function show(visible) {
      bar.classList.toggle('is-visible', visible);
      bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
      bar.inert = !visible;
      document.documentElement.classList.toggle('ng-sticky-atc-on', visible);
    }

    // Visible once the main button has scrolled up past the top of the screen.
    // Measured on scroll rather than with an IntersectionObserver: a jump from
    // below the screen to above it (fast flick, anchor link, scroll restore on
    // reload) never intersects, so an observer would leave the bar hidden.
    var measuring = false;
    function measure() {
      measuring = false;
      var button = main();
      var past = !!button && button.getBoundingClientRect().bottom < 0;
      if (past !== pastMain) {
        pastMain = past;
        show(past);
      }
    }

    function onScroll() {
      if (measuring) return;
      measuring = true;
      requestAnimationFrame(measure);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    function scrollToMain() {
      var button = main();
      if (!button) return;
      button.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    out.button.addEventListener('click', function () {
      var button = main();
      if (!button) return;
      if (button.disabled) {
        scrollToMain();
        return;
      }
      var clickedAt = Date.now();
      button.click();
      // If the page needs something first (a required option, an error
      // message), no cart update follows: take the shopper to the button so
      // they can see why. Wait while the page's own add is still running.
      var waited = 0;
      (function check() {
        if (cartUpdatedAt >= clickedAt) return;
        waited += 300;
        if (mainBusy(button) && waited < 8000) return setTimeout(check, 300);
        if (waited < 1200) return setTimeout(check, 300);
        scrollToMain();
      })();
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        cartUpdatedAt = Date.now();
      });
      // Dawn product pages: keep the summary and image on the chosen variant.
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var data = event && event.data;
        if (!data || !data.variant || !scope.contains(document.getElementById('price-' + data.sectionId))) return;
        if (bar.hasAttribute('data-ng-sticky-summary')) {
          bar.setAttribute('data-ng-sticky-summary', data.variant.title === 'Default Title' ? '' : data.variant.title);
        }
        var media = data.variant.featured_media;
        if (media && media.preview_image) setImage(out.image, media.preview_image.src);
        schedule();
      });
    }

    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (!bar.contains(mutations[i].target)) return schedule();
      }
    }).observe(scope, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class', 'disabled', 'aria-disabled', 'src', 'data-ng-sticky-summary'] });

    sync();
  }

  function initAll() {
    document.querySelectorAll('[data-ng-sticky-atc]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
